import { faEllipsis } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useState, useRef, useEffect } from 'react'
import { ClipLoader } from 'react-spinners'
import styled from 'styled-components'
import theme from '~/design'
import ConfirmModal from '../confirmModal'
import ChatDropdown from './chatDropdown'
import { useLlmChatContext } from '~/components/llm/llmChatContext'
import { useNavigate } from 'react-router-dom'
import llmChatListDefinition from '~/packages/llmChat/list/llmChatList.definition'
import { z } from 'zod'
import { isDevelopment } from '~/utils/shared/platform'

const CardContainer = styled.div<{ $isActive: boolean; $isEditing?: boolean }>`
  position: relative;
  padding: ${props => (props.$isEditing ? `0` : '10px')};
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  background: ${props => (props.$isActive ? `#e2e7e8` : 'white')};

  &:hover {
    background: ${theme.colors.lightGrey};
  }
`

const ChatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const ChatTitle = styled.div`
  font-weight: 500;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  margin-right: 8px;
`

const TitleInput = styled.input`
  font-weight: 500;
  color: #333;
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 9px 10px;
  font-family: inherit;
  font-size: inherit;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
  }
`

const ChatCost = styled.div`
  font-size: 10px;
  color: #666;
  font-family: 'Figtree', Arial, sans-serif;
  white-space: nowrap;

  @media screen and (max-width: 1080px) {
    display: none;
  }
`

const ChatFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ChatDate = styled.div`
  font-size: 12px;
  color: #666;
`

const ChatMoreOptions = styled.div<{ $alwaysVisible?: boolean }>`
  position: absolute;
  right: 0px;
  top: 0px;
  bottom: 0px;
  background: #ecf0f1;
  background: linear-gradient(90deg, rgba(236, 240, 241, 0) 0%, rgba(236, 240, 241, 1) 35%);
  padding: 15px;
  border-radius: 0px 8px 8px 0px;
  display: flex;
  align-items: center;
  justify-content: end;
  width: 60px;
  font-size: 22px;
  opacity: ${props => (props.$alwaysVisible ? 1 : 0)};
  transition: opacity 0.2s ease;

  ${CardContainer}:hover & {
    opacity: 1;
  }

  @media screen and (max-width: 1080px) {
    opacity: 1 !important;
  }
`

const ChatLoading = styled.div`
  position: absolute;
  right: 0px;
  top: 0px;
  bottom: 0px;
  display: flex;
  align-items: center;
  justify-content: end;
  padding: 15px;
  border-radius: 0px 8px 8px 0px;
  width: 60px;
  background: #ecf0f1;
  background: linear-gradient(90deg, rgba(236, 240, 241, 0) 0%, rgba(236, 240, 241, 1) 35%);
`

const formatCost = (totalCostUsd: number | null) => {
  if (!totalCostUsd || totalCostUsd <= 0) return null
  return `$${totalCostUsd.toFixed(6)}`
}

export type Chat = z.infer<typeof llmChatListDefinition.output>['items'][number]

interface ChatCardProps {
  isActive?: boolean
  chat: Chat
}

const ChatCard: React.FC<ChatCardProps> = ({ isActive = false, chat }) => {
  const { onArchiveChat, onRenameChat, onFixChat, onRestoreChat } = useLlmChatContext()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [isRenaming, setIsRenaming] = useState(false)
  const [newTitle, setNewTitle] = useState(chat.title || '')
  const inputRef = useRef<HTMLInputElement>(null)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const navigate = useNavigate()

  const handleClick = () => {
    if (!isRenaming) {
      if (chat.archivedAt) {
        navigate(`/app/ai/archived/${chat.id}`)
      } else {
        navigate(`/app/ai/${chat.id}`)
      }
    }
  }

  const handleMoreOptionsClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsDropdownOpen(prev => !prev)
  }

  const startRenaming = (chatId: number) => {
    setIsDropdownOpen(false)
    setIsRenaming(true)
    setNewTitle(chat.title || '')
  }

  const handleArchive = (chatId: number) => {
    setIsDropdownOpen(false)
    setIsModalOpen(true)
  }

  const handleConfirmArchive = async () => {
    const result = await onArchiveChat?.(chat.id)
    if (result) {
      setIsModalOpen(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewTitle(e.target.value)
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      finishRenaming()
    } else if (e.key === 'Escape') {
      cancelRenaming()
    }
  }

  const handleInputBlur = () => {
    finishRenaming()
  }

  const finishRenaming = async () => {
    if (newTitle.trim() && newTitle !== chat.title) {
      setIsLoading(true)
      await onRenameChat?.(chat.id, newTitle)
    }
    setIsRenaming(false)
    setIsLoading(false)
  }

  const cancelRenaming = () => {
    setIsRenaming(false)
    setNewTitle(chat.title || '')
  }

  const handleFixChat = async (chatId: number, isFixed: boolean) => {
    setIsDropdownOpen(false)
    setIsLoading(true)
    await onFixChat?.(chatId, isFixed)
    setIsLoading(false)
  }

  const handleRestoreChat = async (chatId: number) => {
    setIsDropdownOpen(false)
    setIsLoading(true)
    await onRestoreChat?.(chatId)
    setIsLoading(false)
  }

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isRenaming])

  useEffect(() => {
    if (!dropdownRef.current) return

    const handleScrollOrResize = () => {
      setIsDropdownOpen(false)
    }

    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [dropdownRef.current])

  return (
    <>
      <CardContainer $isActive={isActive} $isEditing={isRenaming} onClick={handleClick}>
        <ChatHeader>
          {isRenaming ? (
            <TitleInput
              ref={inputRef}
              value={newTitle}
              disabled={isLoading}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              onBlur={handleInputBlur}
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <ChatTitle>{chat.title}</ChatTitle>
          )}
          {isDevelopment && chat.totalCostUsd !== undefined && !isRenaming && (
            <ChatCost>{formatCost(chat.totalCostUsd)}</ChatCost>
          )}
        </ChatHeader>
        {/* <ChatFooter>
        <ChatDate>{formatDate(chat.createdAt)}</ChatDate>
      </ChatFooter> */}
        {!isRenaming && (
          <ChatMoreOptions
            $alwaysVisible={isDropdownOpen}
            ref={dropdownRef}
            onClick={handleMoreOptionsClick}
          >
            <FontAwesomeIcon icon={faEllipsis} />
          </ChatMoreOptions>
        )}
        {isLoading && (
          <ChatLoading>
            <ClipLoader size={18} color='#333' speedMultiplier={0.5} />
          </ChatLoading>
        )}
      </CardContainer>

      {chat.archivedAt ? (
        <ChatDropdown
          isOpen={isDropdownOpen}
          isFixed={false}
          onClose={() => setIsDropdownOpen(false)}
          onRestore={() => handleRestoreChat(chat.id)}
          anchorElement={dropdownRef.current}
        />
      ) : (
        <ChatDropdown
          isOpen={isDropdownOpen}
          isFixed={chat.isFixed}
          onClose={() => setIsDropdownOpen(false)}
          onFix={() => handleFixChat(chat.id, !chat.isFixed)}
          onRename={() => startRenaming(chat.id)}
          onArchive={() => handleArchive(chat.id)}
          anchorElement={dropdownRef.current}
        />
      )}

      {isModalOpen && (
        <ConfirmModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleConfirmArchive}
          title='Arquivar chat?'
          description='Tem certeza que deseja arquivar esse chat?'
        />
      )}
    </>
  )
}

export default ChatCard
